// Product analytics, on two surfaces with two different answers to one question:
// may we measure this visitor at all?
//
// THE CONSOLE IS OURS, behind a sign-in, governed by no notice, and the people
// in it are our own customers. It measures on load, exactly like Featurebase.
//
// THE WEBSITE IS NOT. layouts/website.html.erb runs OUR OWN banner on
// yupkit.com, so a visitor there has been asked, and measuring them before they
// answer is the counterexample this product exists to argue against.
//
// AND THE SCRIPT GATE DOES NOT CATCH THIS ONE. That is the whole reason this
// file is careful rather than a copy of lib/featurebase.tsx. The gate in
// embed/hold.ts patches appendChild and insertBefore and neutralises third-party
// <script src> nodes -- which is how Featurebase, whose SDK injects a tag at
// runtime, is held without anybody arranging it. posthog-js is an npm import:
// it is bundled into our own first-party output, inserts no script, and reaches
// its ingestion host over fetch. Nothing holds it. Installed the ordinary way it
// would set ph_* cookies and ship pageviews in front of our own notice, and the
// gate would report a clean page while it happened.
//
// So the hold is written here, in code, and it is the only thing standing
// between the site and that. Two halves, and both matter:
//
//   1. `opt_out_capturing_by_default` -- nothing is captured, no cookie is set
//      and no request is made until something opts this visitor in.
//   2. __tcfapi, which is the banner's own API and fires when the visitor
//      answers. Purposes 1 and 8 are the two the measurement needs, and both
//      must be an explicit true.
//
// Purpose 1 is "store and/or access information on a device", which is what
// licenses the cookies, and it is consent-only under TCF -- there is no
// legitimate interest to fall back on, which is correct and is why it is the
// purpose worth keying on. Purpose 8 is "measure content performance", which is
// what the measuring itself is. See docs/banner-spec.md.
import type { Page } from '@inertiajs/core'
import type { PostHog } from 'posthog-js'
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

/** What the server sends when a key is configured, and nothing when it is not. */
export interface PostHogSettings {
  key: string
  host: string
  surface: 'app' | 'website'
}

/**
 * The two purposes the measurement needs, both consent-only in practice.
 *
 * 1 -- store and/or access information on a device (the ph_* cookies)
 * 8 -- measure content performance (the events)
 */
const REQUIRED_PURPOSES = [1, 8] as const

type Consents = Record<number, boolean | undefined>

/**
 * Whether a decision licenses measuring.
 *
 * EVERY PURPOSE MUST BE AN EXPLICIT `true`. getTCData reports an unset bit as
 * ABSENT rather than as false, so a truthiness test and a `!== false` test both
 * read "never asked" as a yes. Exported because this is the rule worth a test of
 * its own, rather than something only reachable by booting an SDK.
 */
export function licensed(consents: Consents | undefined): boolean {
  if (!consents) return false

  return REQUIRED_PURPOSES.every((purpose) => consents[purpose] === true)
}

interface TcData {
  purpose?: { consents?: Consents }
}

type Tcfapi = (
  command: string,
  version: number,
  callback: (data: TcData, success: boolean) => void,
) => void

/**
 * Every option, and the two surfaces answering the same questions differently.
 *
 * ON THE CONSOLE, ELEMENT TEXT IS SOMEBODY ELSE'S PERSONAL DATA. The screens
 * render consent records, visitor addresses, hostnames and vendor lists that we
 * hold AS A PROCESSOR for a customer, under their instruction and not ours.
 * Shipping that to an analytics vendor is processing they never asked for, and
 * the processing terms we publish say we do not do it. So the console captures
 * the shape of an interaction -- which element, which page -- and none of the
 * words in it.
 *
 * On the website the text is our own marketing copy, and the button somebody
 * pressed is the entire point of measuring a marketing page.
 */
export function optionsFor({ host, surface }: PostHogSettings) {
  const masked = surface === 'app'

  return {
    api_host: host,
    // Pinned rather than left to float. PostHog changes what its defaults mean
    // by date, and a default that quietly starts capturing something new is the
    // one kind of change this integration must not take silently.
    defaults: '2026-08-30' as const,
    // NOT REPLAY, ON EITHER SURFACE. On the console it would record the
    // customer data described above frame by frame, which no amount of masking
    // makes somebody else's data ours to send.
    disable_session_recording: true,
    disable_surveys: true,
    // THE THREE THAT ARRIVE WITH REMOTE CONFIG. Turning flags on for the console
    // re-enabled the remote config fetch, and with it PostHog downloaded
    // dead-clicks-autocapture.js and web-vitals-with-attribution.js -- neither
    // of which had ever loaded while flags were off. The project has both
    // switched OFF server-side, and they were fetched anyway, so the server
    // setting governs whether they RUN and not whether they arrive. Declared
    // here, where it decides both.
    capture_dead_clicks: false,
    capture_performance: false,
    capture_heatmaps: false,
    disable_web_experiments: true,
    // FLAGS ON THE CONSOLE, NEVER ON THE WEBSITE, and the reason is not consent.
    //
    // The endpoint is a request to a third party carrying an identifier, and it
    // was still firing twelve seconds after a visitor withdrew, on a page where
    // capture had correctly stopped. Gating it on consent like everything else
    // would answer that. What it would not answer is the CDN: the marketing
    // site is one CloudFront response handed to every visitor, with
    // cookie_behavior and query_string_behavior both "none" (infra/modules/
    // website/main.tf), so a variant cannot be chosen at the origin and cannot
    // be chosen by URL either -- /?variant=b serves the cached bytes of /.
    // A website experiment is therefore client-side, after paint, on the
    // consented subset only, which is a different feature with a sampling
    // caveat attached. Until that is designed, off here.
    advanced_disable_flags: surface === 'website',
    autocapture: true as const,
    mask_all_text: masked,
    mask_all_element_attributes: masked,
    // AND THE TITLE, WHICH IS THE ONE THAT GOT THROUGH. Masking the elements
    // left `$pageview.properties.title` carrying "Overview - northgate.edu",
    // because PageTitle's format is "<page> - <which yupkit. this is>" and
    // behind the sign-in that second half is the tenant's own hostname. Found
    // by reading a real payload, not by reading the config: every element
    // property was clean and the customer was still named on every pageview.
    //
    // Which workspace somebody is in is a fair thing to measure, but it should
    // be a property we chose to send, not a string that arrived inside a tab
    // caption. Dropped here; `surface` stays.
    property_denylist: masked ? ['title', '$title'] : [],
    // gclid, fbclid and the rest, stripped from the URL before it is a property.
    mask_personal_data_properties: true,
    // BELT AND BRACES on the website, where the download is already gated on
    // consent: if the import below ever moves earlier, this is what keeps the
    // SDK silent until someone opts this visitor in.
    opt_out_capturing_by_default: surface === 'website',
    // AND NOT CAPTURING IS NOT THE SAME AS NOT STORING. Without this,
    // opt_out_capturing stops the events and leaves the ph_* cookies on the
    // device -- measured, on a visitor who had just refused purpose 1, which is
    // the purpose that licenses storage and the one thing they said no to.
    opt_out_persistence_by_default: surface === 'website',
  }
}

/**
 * THE SDK IS NOT IN THE PAGE'S BUNDLE, and that is a measurement rather than a
 * preference. Imported statically it put 95 KB gzipped into the one chunk every
 * page of both surfaces loads, taking it from 105 KB to 200 KB.
 *
 * On the website that cost lands on the critical path of a marketing page whose
 * LCP is already tight, to run a tool that may not capture anything at all --
 * so a visitor who refuses never downloads it, which is faster AND is the
 * behaviour the product argues for. On the console it is deferred rather than
 * withheld: nothing measured is worth a slower first paint.
 */
async function boot(settings: PostHogSettings): Promise<PostHog> {
  const { default: posthog } = await import('posthog-js')

  posthog.init(settings.key, optionsFor(settings))
  posthog.register({ surface: settings.surface })

  return posthog
}

/**
 * Follow the visitor's decision for as long as the tab lives.
 *
 * addEventListener rather than getTCData, because this has to run on the answer
 * and not only on whatever was true at boot: a visitor who accepts is measured
 * from that moment without a reload, and a visitor who withdraws stops being
 * measured at the moment they withdraw rather than at their next navigation.
 *
 * OPT OUT IS AN ACTIVE STEP, not the absence of opting in. A decision can go
 * both ways and the second direction is the one a consent product is judged on.
 */
function followDecision(
  api: Tcfapi,
  settings: PostHogSettings,
  ready: (ph: PostHog) => void,
): void {
  let client: PostHog | null = null
  let loading = false
  let measuring = false

  api('addEventListener', 2, (data, success) => {
    if (!success) return

    if (!licensed(data?.purpose?.consents)) {
      if (!client) return

      client.opt_out_capturing()
      // WITHDRAWAL TAKES THE IDENTIFIERS WITH IT. Opting out stops the events;
      // reset drops the distinct id and the stored properties, so somebody who
      // changes their mind is not still carrying the id they were measured
      // under. Only on the way DOWN from measuring -- the TCF API fires
      // repeatedly, and resetting on every one of those would be throwing away
      // a session for a visitor who never consented in the first place.
      if (measuring) client.reset()
      measuring = false
      return
    }

    if (client) {
      client.opt_in_capturing()
      measuring = true
      return
    }

    // The first yes is what pays for the download. Guarded because the TCF API
    // fires more than once and two boots would be two SDKs.
    if (loading) return
    loading = true

    void boot(settings).then((posthog) => {
      client = posthog
      posthog.opt_in_capturing()
      measuring = true
      ready(posthog)
    })
  })
}

/**
 * The loaded client, or null while it is still loading or was never allowed.
 *
 * OURS RATHER THAN PostHogContext, and that is the whole reason flags are
 * usable without giving back the 94 KB. A page reaching for posthog-js/react's
 * own hook would be a STATIC import of posthog-js, in a chunk the website
 * shares, which is exactly the cost `boot` exists to avoid. Nothing outside
 * this file imports posthog-js at all; the type import above is erased.
 */
const ClientContext = createContext<PostHog | null>(null)

/**
 * Read a feature flag. `undefined` until the flags have loaded, and forever on
 * the website, where the endpoint is off -- so a caller has to have a sensible
 * answer for "not yet", and the honest default is the control.
 *
 * THIS CALLS getFeatureFlag RATHER THAN READING A SNAPSHOT, which looks like a
 * detail and is the difference between an experiment that reports and one that
 * silently never does: getFeatureFlag sends `$feature_flag_called`, and that
 * event IS the exposure PostHog computes results from. A map of values read
 * from onFeatureFlags would render the right variant and record nobody seeing
 * it.
 */
export function useFeatureFlag(key: string): boolean | string | undefined {
  const client = useContext(ClientContext)
  const [value, setValue] = useState<boolean | string | undefined>(undefined)

  useEffect(() => {
    if (!client) return

    const read = () => setValue(client.getFeatureFlag(key))
    read()

    // Re-read when the flags arrive or are reloaded, and drop the listener with
    // the component. onFeatureFlags hands back its own unsubscribe.
    return client.onFeatureFlags(read)
  }, [client, key])

  return value
}

/**
 * Renders its children immediately and the provider once there is a client, so
 * nothing in the app ever waits on analytics loading -- or on a visitor
 * answering a banner, which may be never.
 */
function Analytics({
  settings,
  children,
}: {
  settings: PostHogSettings
  children: ReactNode
}): ReactElement {
  const [client, setClient] = useState<PostHog | null>(null)

  useEffect(() => {
    if (settings.surface === 'app') {
      void boot(settings).then(setClient)
      return
    }

    const api = (window as Window & { __tcfapi?: Tcfapi }).__tcfapi
    if (!api) {
      // NO BANNER MEANS NO PERMISSION, not permission by default. The loader is
      // absent whenever WEBSITE_BANNER_KEY is unset, and an analytics tool that
      // measures freely whenever the consent tooling fails to load is the exact
      // failure this file is written against. Staying silent costs us data; the
      // other way costs the visitor a decision.
      console.info('[yupkit] no consent API on the page, so nothing is measured')
      return
    }

    followDecision(api, settings, setClient)
    // Once per tab. The settings are a constant for the life of the document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <ClientContext.Provider value={client}>{children}</ClientContext.Provider>
}

export function withPostHog(
  app: ReactElement,
  { ssr, page }: { ssr: boolean; page: Page },
): ReactElement {
  // Read from the INITIAL page rather than on every visit, which is what keeps
  // this to one boot per tab. Absent wherever no key is configured, which is
  // every environment but production.
  const settings = ssr ? null : (page.props as { posthog?: PostHogSettings | null }).posthog
  if (!settings) return app

  return <Analytics settings={settings}>{app}</Analytics>
}
